import { css } from '@emotion/react';
import { urls } from '@zapier/identity-utils';
import { atoms } from '@zapier/marketing-ui';

import { SocialOptionsSeparator } from '../global/SocialOptionsSeparator';
import { Invitation, useLogin } from '../../hooks';
import { QueryParams, RouteTo } from '../../types';
import { Captcha } from '../global/Captcha';
import { AltAuthCta } from './AltAuthCta';
import { EmailPasswordForm } from './EmailPasswordForm';
import { NewDeviceConfirmationPanel } from './NewDeviceConfirmationPanel';
import { NoAccountLink } from './NoAccountLink';
import { SocialItems } from './SocialItems';
import { WelcomeBackMessage } from './WelcomeBackMessage';

export type LoginFormProps = {
  /**
   * Autofocus the email field when rendering the login form
   */
  enableEmailAutoFocus?: boolean;
  /**
   * Key used by Google reCaptcha
   */
  googleCaptchaSiteKey: string;
  /**
   * Will ignore foreversession previous login when true
   */
  forceLoginDisplayForm?: boolean;
  /**
   * Forces users to log in to email account associated with invitation
   */
  invitation?: Invitation | null;
  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string | undefined;
  /**
   * Current query params (so they can be preserved when routing)
   */
  queryParams?: QueryParams;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: RouteTo;
  /**
   * Show the signup link (default to true)
   */
  showSignupLink?: boolean;

  /**
   * Should we render Google SSO?
   */
  googleSSOEnabled?: boolean;

  /**
   * Should we render Facebook SSO?
   */
  facebookSSOEnabled?: boolean;

  /**
   * Should we render Microsoft SSO?
   */
  microsoftSSOEnabled?: boolean;

  /**
   * Should we render Continue with SSO?
   */
  SAMLSSOEnabled?: boolean;
};

const FORM_ERROR_ID = 'login-form-error';

const rootStyle = css`
  display: grid;
  grid-gap: 20px;
`;

// The auth column is shrink-to-fit on mobile; the error wraps in it instead of widening it.
const formErrorStyle = css`
  contain: inline-size;
`;

/**
 * Displays form to allow the user to log in. Supports username / password, SSO, SAML
 */
export function LoginForm({
  enableEmailAutoFocus = false,
  forceLoginDisplayForm = false,
  googleCaptchaSiteKey,
  invitation,
  next = urls.DEFAULT_LOGIN_NEXT,
  queryParams = {},
  routeTo,
  showSignupLink = true,
  googleSSOEnabled = true,
  microsoftSSOEnabled = true,
  facebookSSOEnabled = true,
  SAMLSSOEnabled = true,
}: LoginFormProps) {
  const {
    canSubmit,
    formData,
    formErrors,
    handleChange,
    handleClearForm,
    handleSubmit,
    isCaptchaRequired,
    newDeviceCodeStep,
    previousAuthType,
    shouldShowEmailPasswordForm,
    shouldShowEmailField,
    shouldShowFacebookSSO,
    shouldShowGoogleSSO,
    shouldShowMicrosoftSSO,
    shouldShowSAMLSSO,
    shouldShowPasswordField,
    status,
    submittedFields,
  } = useLogin({
    email: invitation?.email,
    defaultEmail: (queryParams?.email as string) || '',
    forceLoginDisplayForm,
    next,
    routeTo,
    googleSSOEnabled,
    microsoftSSOEnabled,
    facebookSSOEnabled,
    SAMLSSOEnabled: SAMLSSOEnabled,
  });

  const showSSOButtons = [
    googleSSOEnabled,
    microsoftSSOEnabled,
    facebookSSOEnabled,
    SAMLSSOEnabled,
  ].find(enabled => enabled === true || enabled === undefined);

  const hasSocial =
    shouldShowFacebookSSO ||
    shouldShowGoogleSSO ||
    shouldShowMicrosoftSSO ||
    shouldShowSAMLSSO;

  const isWelcomeBackShown =
    (!forceLoginDisplayForm || submittedFields.email) &&
    (!shouldShowEmailField || previousAuthType);
  const isSeparatorShown =
    shouldShowEmailPasswordForm &&
    shouldShowEmailField &&
    hasSocial &&
    showSSOButtons;
  const isAltAuthCtaShown =
    (!forceLoginDisplayForm || submittedFields.email) &&
    previousAuthType &&
    shouldShowEmailField;

  const isEmailFieldDisabled = Boolean(invitation?.email);

  /** Undefined when the visible field already shows the same sentence (it would be read twice). */
  const announcedFormError = (() => {
    if (isCaptchaRequired || status !== 'error') {
      return undefined;
    }
    // A disabled (invitation) email field can't take focus, so the region reads its error too.
    if (shouldShowEmailField && isEmailFieldDisabled) {
      return formErrors.error || formErrors.email;
    }
    if (!formErrors.error) {
      return undefined;
    }
    const errorOnTheVisibleField = shouldShowPasswordField
      ? submittedFields.password && formErrors.password
      : submittedFields.email && formErrors.email;
    const isEchoedByAField =
      typeof errorOnTheVisibleField === 'string' &&
      errorOnTheVisibleField.trim() === formErrors.error.trim();
    return isEchoedByAField ? undefined : formErrors.error;
  })();

  // Always mounted, empty (a live region must exist before its message); same key keeps it across the captcha view.
  const formErrorRegion = (
    <div
      key="form-error-region"
      css={formErrorStyle}
      className={
        announcedFormError && announcedFormError === formErrors.error
          ? `${atoms.bodySM} ${atoms.textError} ${atoms.textCenter}`
          : atoms.srOnly
      }
      role="alert"
      id={FORM_ERROR_ID}
    >
      {announcedFormError ?? null}
    </div>
  );

  // Only show Captcha if it's required
  if (isCaptchaRequired) {
    return (
      <section css={rootStyle}>
        <Captcha
          googleCaptchaSiteKey={googleCaptchaSiteKey}
          onCaptchaVerification={(captcha: string | null) => {
            handleChange('captcha', captcha || '');
          }}
        />
        {formErrorRegion}
      </section>
    );
  }

  // The password was correct, but this browser needs the emailed code first
  if (newDeviceCodeStep.challenge) {
    return (
      <section css={rootStyle} data-testid="new-device-confirmation">
        <NewDeviceConfirmationPanel
          challenge={newDeviceCodeStep.challenge}
          email={formData.email}
          googleCaptchaSiteKey={googleCaptchaSiteKey}
          step={newDeviceCodeStep}
        />
      </section>
    );
  }

  return (
    <section css={rootStyle}>
      {isWelcomeBackShown && (
        <WelcomeBackMessage
          email={previousAuthType && shouldShowEmailField ? '' : formData.email}
          onClear={handleClearForm}
        />
      )}
      {hasSocial && shouldShowEmailField && showSSOButtons && (
        <SocialItems
          next={next}
          selectedApis={queryParams?.selectedApis || []}
          shouldShowFacebookSSO={shouldShowFacebookSSO}
          shouldShowGoogleSSO={shouldShowGoogleSSO}
          shouldShowMicrosoftSSO={shouldShowMicrosoftSSO}
          shouldShowSAMLSSO={shouldShowSAMLSSO}
          queryParams={queryParams}
        />
      )}
      {isSeparatorShown && <SocialOptionsSeparator />}
      {shouldShowEmailPasswordForm && (
        <EmailPasswordForm
          canSubmit={canSubmit}
          formData={formData}
          formErrorId={announcedFormError ? FORM_ERROR_ID : undefined}
          formErrors={formErrors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          shouldDisableEmailField={isEmailFieldDisabled}
          shouldFocusEmailField={Boolean(enableEmailAutoFocus)}
          shouldShowEmailField={shouldShowEmailField}
          shouldShowPasswordField={shouldShowPasswordField}
          status={status}
          submittedFields={submittedFields}
        />
      )}
      {formErrorRegion}
      {isAltAuthCtaShown && previousAuthType && showSSOButtons && (
        <AltAuthCta authType={previousAuthType} />
      )}
      {showSignupLink && <NoAccountLink queryParams={queryParams} />}
    </section>
  );
}
